/**
 * @fileoverview Session Time Form Component
 * @author Hospital Room Scheduler Team
 * @created 08-10-2025 02:45:00
 * 
 * Modal form for creating and editing session times for rooms.
 * Allows admins and coordinators to define when rooms are available for scheduling.
 * 
 * Features:
 * - Create new session times with name, start/end times, buffer minutes
 * - Edit existing session times
 * - Validation for time conflicts and business rules
 * - Real-time validation and error handling
 * - Accessibility support with proper ARIA labels
 */

import React, { useState, useEffect } from 'react';
import {
  Modal,
  Stack,
  Text,
  TextInput,
  NumberInput,
  Button,
  Group,
  Alert,
  Switch,
  Divider,
  ActionIcon,
  Tooltip,
} from '@mantine/core';
import { TimeInput } from '@mantine/dates';
import {
  IconClock,
  IconCheck,
  IconX,
  IconInfoCircle,
  IconAlertTriangle,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useForm } from '@mantine/form';
import { useCreateSessionTime, useUpdateSessionTime } from '../../hooks/admin/useSessionTimes';

// =============================================================================
// TYPES & INTERFACES
// =============================================================================

/**
 * Session time data structure
 */
export interface SessionTime {
  id: string;
  name: string;
  startTime: string;
  endTime: string;
  bufferMinutes: number;
  isActive: boolean;
  roomId: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Session time form data
 */
export interface SessionTimeFormData {
  name: string;
  startTime: string;
  endTime: string;
  bufferMinutes: number;
  isActive: boolean;
}

/**
 * SessionTimeForm component props
 */
export interface SessionTimeFormProps {
  /** Control modal open state */
  opened: boolean;
  
  /** Callback when modal closes */
  onClose: () => void;
  
  /** Callback when form is submitted successfully */
  onSubmit: (sessionTime: SessionTime) => void;
  
  /** Room ID to create session time for */
  roomId: string;
  
  /** Existing session time to edit (optional) */
  sessionTime?: SessionTime;
  
  /** Loading state */
  loading?: boolean;
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

/**
 * Session Time Form Component
 * 
 * Modal form for creating and editing session times for rooms.
 * Provides validation for time conflicts and business rules.
 * 
 * @component
 * @example
 * ```tsx
 * <SessionTimeForm
 *   opened={showModal}
 *   onClose={() => setShowModal(false)}
 *   onSubmit={(sessionTime) => console.log('Created:', sessionTime)}
 *   roomId="room-123"
 *   sessionTime={existingSessionTime} // For editing
 * />
 * ```
 */
export function SessionTimeForm({
  opened,
  onClose,
  onSubmit,
  roomId,
  sessionTime,
  loading: _ = false,
}: SessionTimeFormProps): React.ReactElement {
  
  // =============================================================================
  // STATE & FORM SETUP
  // =============================================================================
  
  const [submitting, setSubmitting] = useState(false);
  
  // Form setup with validation
  const form = useForm<SessionTimeFormData>({
    initialValues: {
      name: '',
      startTime: '09:00',
      endTime: '17:00',
      bufferMinutes: 0,
      isActive: true,
    },
    validate: {
      name: (value) => {
        if (!value || value.trim().length === 0) {
          return 'Session name is required';
        }
        if (value.trim().length < 2) {
          return 'Session name must be at least 2 characters';
        }
        if (value.trim().length > 50) {
          return 'Session name must be less than 50 characters';
        }
        return null;
      },
      startTime: (value) => {
        if (!value) return 'Start time is required';
        return null;
      },
      endTime: (value) => {
        if (!value) return 'End time is required';
        return null;
      },
      bufferMinutes: (value) => {
        if (value < 0) return 'Buffer minutes cannot be negative';
        if (value > 120) return 'Buffer minutes cannot exceed 120 minutes';
        return null;
      },
    },
    validateInputOnBlur: true,
  });
  
  // =============================================================================
  // API HOOKS
  // =============================================================================
  
  const createSessionTimeMutation = useCreateSessionTime();
  const updateSessionTimeMutation = useUpdateSessionTime();
  
  // =============================================================================
  // EFFECTS
  // =============================================================================
  
  // Reset form when modal opens/closes or session time changes
  useEffect(() => {
    if (opened) {
      if (sessionTime) {
        // Edit mode - populate form with existing data
        form.setValues({
          name: sessionTime.name,
          startTime: sessionTime.startTime,
          endTime: sessionTime.endTime,
          bufferMinutes: sessionTime.bufferMinutes,
          isActive: sessionTime.isActive,
        });
      } else {
        // Create mode - reset to defaults
        form.setValues({
          name: '',
          startTime: '09:00',
          endTime: '17:00',
          bufferMinutes: 0,
          isActive: true,
        });
      }
      form.clearErrors();
    }
  }, [opened, sessionTime]);
  
  // =============================================================================
  // VALIDATION HELPERS
  // =============================================================================
  
  /**
   * Validate that end time is after start time
   */
  const validateTimeRange = (startTime: string, endTime: string): string | null => {
    if (!startTime || !endTime) return null;
    
    const start = new Date(`2000-01-01T${startTime}:00`);
    const end = new Date(`2000-01-01T${endTime}:00`);
    
    if (end <= start) {
      return 'End time must be after start time';
    }
    
    // Check minimum session duration (30 minutes)
    const duration = (end.getTime() - start.getTime()) / (1000 * 60);
    if (duration < 30) {
      return 'Session must be at least 30 minutes long';
    }
    
    return null;
  };
  
  // =============================================================================
  // EVENT HANDLERS
  // =============================================================================
  
  /**
   * Handle form submission
   */
  const handleSubmit = async (values: SessionTimeFormData) => {
    // Validate time range
    const timeError = validateTimeRange(values.startTime, values.endTime);
    if (timeError) {
      form.setFieldError('endTime', timeError);
      return;
    }
    
    setSubmitting(true);
    
    try {
      const sessionTimeData = {
        ...values,
        roomId,
      };
      
      let result: SessionTime;
      
      if (sessionTime) {
        // Update existing session time
        result = await updateSessionTimeMutation.mutateAsync({
          id: sessionTime.id,
          sessionTimeData,
        }) as SessionTime;
      } else {
        // Create new session time
        result = await createSessionTimeMutation.mutateAsync(sessionTimeData) as SessionTime;
      }
      
      notifications.show({
        title: sessionTime ? 'Session Time Updated' : 'Session Time Created',
        message: `Session "${values.name}" has been ${sessionTime ? 'updated' : 'created'} successfully`,
        color: 'green',
        icon: <IconCheck size={16} />,
      });
      
      onSubmit(result);
      onClose();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to save session time',
        color: 'red',
        icon: <IconX size={16} />,
      });
    } finally {
      setSubmitting(false);
    }
  };
  
  /**
   * Handle modal close
   */
  const handleClose = () => {
    if (!submitting) {
      form.reset();
      onClose();
    }
  };
  
  // =============================================================================
  // RENDER
  // =============================================================================
  
  const isEditing = !!sessionTime;
  const isSubmitting = submitting || createSessionTimeMutation.isPending || updateSessionTimeMutation.isPending;
  
  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group gap="sm">
          <IconClock size={20} />
          <Text fw={600}>
            {isEditing ? 'Edit Session Time' : 'Create Session Time'}
          </Text>
        </Group>
      }
      size="md"
      centered
      closeOnClickOutside={!isSubmitting}
      closeOnEscape={!isSubmitting}
      trapFocus
      returnFocus
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          {/* Session Name */}
          <TextInput
            label="Session Name"
            placeholder="e.g., Morning Session, Afternoon Session"
            required
            {...form.getInputProps('name')}
            leftSection={<IconClock size={16} />}
            disabled={isSubmitting}
          />
          
          {/* Time Range */}
          <Group grow>
            <TimeInput
              label="Start Time"
              placeholder="Select start time"
              required
              {...form.getInputProps('startTime')}
              disabled={isSubmitting}
            />
            <TimeInput
              label="End Time"
              placeholder="Select end time"
              required
              {...form.getInputProps('endTime')}
              disabled={isSubmitting}
            />
          </Group>
          
          {/* Buffer Minutes */}
          <NumberInput
            label={
              <Group gap="xs">
                <Text>Buffer Minutes</Text>
                <Tooltip label="Additional time added before and after the session for preparation and cleanup">
                  <ActionIcon size="xs" variant="subtle" color="gray">
                    <IconInfoCircle size={12} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            }
            placeholder="0"
            min={0}
            max={120}
            {...form.getInputProps('bufferMinutes')}
            disabled={isSubmitting}
            rightSection={<Text size="sm" c="dimmed">min</Text>}
          />
          
          {/* Active Status */}
          <Switch
            label="Active Session"
            description="Inactive sessions cannot be used for new assignments"
            {...form.getInputProps('isActive', { type: 'checkbox' })}
            disabled={isSubmitting}
          />
          
          {/* Validation Errors */}
          {form.errors['endTime'] && (
            <Alert
              icon={<IconAlertTriangle size={16} />}
              title="Time Validation Error"
              color="red"
              variant="light"
            >
              {form.errors['endTime']}
            </Alert>
          )}
          
          <Divider />
          
          {/* Action Buttons */}
          <Group justify="flex-end" gap="sm">
            <Button
              variant="light"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={isSubmitting}
              disabled={!form.isValid()}
              leftSection={<IconCheck size={16} />}
            >
              {isEditing ? 'Update Session' : 'Create Session'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}